import type { CreateIssueRequest } from "@qualtio-workhub/contracts";
import { Button, Input, Modal, Select, Textarea } from "@qualtio-workhub/ui";
import { type FormEvent, useState } from "react";
import { createIssue, listAgents, listContextUsers } from "../../shared/api/client";
import { useWorkhubQuery } from "../../shared/api/hooks";
import { useAuth } from "../../shared/auth/auth-provider";
import { useI18n } from "../../shared/i18n/provider";
import { trimmedOrUndefined } from "../../shared/string";
import {
  priorityLabelKeys,
  priorityOptions,
  statusLabelKeys,
  statusOptions,
  typeLabelKeys,
  typeOptions,
} from "./model";
import { UserAutocomplete } from "./user-autocomplete-input";

export function CreateIssueModal({
  defaultContext = {},
  open,
  onClose,
  onCreated,
}: {
  defaultContext?: Pick<Partial<CreateIssueRequest>, "chapterId" | "projectId">;
  open: boolean;
  onClose: () => void;
  onCreated(issueKey: string): void;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const agents = useWorkhubQuery(listAgents);
  const users = useWorkhubQuery(listContextUsers);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<CreateIssueRequest["priority"]>("none");
  const [type, setType] = useState<CreateIssueRequest["type"]>("task");
  const [status, setStatus] = useState<CreateIssueRequest["status"]>("triage");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [assigneeAgentId, setAssigneeAgentId] = useState("");
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (auth.token === null || title.trim().length === 0) {
      setError(true);
      return;
    }

    setSaving(true);
    setError(false);
    try {
      const issue = await createIssue(auth.token, {
        title,
        description: trimmedOrUndefined(description),
        priority,
        type,
        status,
        assigneeUserId: assigneeUserId || undefined,
        assigneeAgentId: assigneeAgentId || undefined,
        ...defaultContext,
        labelIds: [],
      });
      setTitle("");
      setDescription("");
      setPriority("none");
      setType("task");
      setStatus("triage");
      setAssigneeUserId("");
      setAssigneeAgentId("");
      onCreated(issue.key);
      onClose();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      closeLabel={t("common.close")}
      initialFocusId="create-title"
      open={open}
      title={t("issue.create.title")}
      onClose={onClose}
    >
      <form className="work-form" onSubmit={onSubmit}>
        <label className="field" htmlFor="create-title">
          <span>{t("issue.field.title")}</span>
          <Input
            autoFocus
            id="create-title"
            required
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
          />
        </label>
        <label className="field" htmlFor="create-description">
          <span>{t("issue.field.description")}</span>
          <Textarea
            id="create-description"
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />
        </label>
        <div className="form-grid">
          <label className="field" htmlFor="create-priority">
            <span>{t("issue.field.priority")}</span>
            <Select
              id="create-priority"
              value={priority}
              onChange={(event) =>
                setPriority(event.currentTarget.value as CreateIssueRequest["priority"])
              }
            >
              {priorityOptions.map((option) => (
                <option key={option} value={option}>
                  {t(priorityLabelKeys[option])}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="create-type">
            <span>{t("issue.field.type")}</span>
            <Select
              id="create-type"
              value={type}
              onChange={(event) => setType(event.currentTarget.value as CreateIssueRequest["type"])}
            >
              {typeOptions.map((option) => (
                <option key={option} value={option}>
                  {t(typeLabelKeys[option])}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="create-status">
            <span>{t("issue.field.status")}</span>
            <Select
              id="create-status"
              value={status}
              onChange={(event) =>
                setStatus(event.currentTarget.value as CreateIssueRequest["status"])
              }
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {t(statusLabelKeys[option])}
                </option>
              ))}
            </Select>
          </label>
          <label className="field" htmlFor="create-owner">
            <span>{t("issue.field.assigneeUser")}</span>
            <UserAutocomplete
              clearLabel={t("issue.owner.clear")}
              id="create-owner"
              noResultsLabel={t("issue.userAutocomplete.noResults")}
              placeholder={t("issue.owner.placeholder")}
              selectedUserId={assigneeUserId || null}
              users={users.data ?? []}
              onSelectedUserIdChange={(userId) => setAssigneeUserId(userId ?? "")}
            />
          </label>
          <label className="field" htmlFor="create-agent">
            <span>{t("issue.field.agent")}</span>
            <Select
              id="create-agent"
              value={assigneeAgentId}
              onChange={(event) => setAssigneeAgentId(event.currentTarget.value)}
            >
              <option value="">{t("issue.unassigned")}</option>
              {(agents.data ?? []).map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </Select>
          </label>
        </div>
        {error ? <p className="error-text">{t("issue.create.error")}</p> : null}
        <div className="form-actions">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={saving || title.trim().length === 0} type="submit">
            {saving ? t("common.saving") : t("issue.create.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
